import React, { useEffect, useState, useCallback } from "react";
import Swal from "sweetalert2";
import axiosWithAuth from "../../../../utils/axiosWithAuth";
import "./SpaceManager.scss";

const SpaceManager = () => {
  const [spaceAdmin, setSpaceAdmin] = useState([]);
  const [space, setSpace] = useState({
    name: { az: "", en: "", ru: "" },
    status: true,
  });
  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);
  const [loading, setLoading] = useState(false);
  const authToken = localStorage.getItem("authToken");

  const fetchSpace = useCallback(() => {
    if (!authToken) return;
    
    setLoading(true);
    const api = axiosWithAuth();
    api.get("/spaces/admin")
      .then((response) => {
        const activeSpaces = response.data.filter(space => space.status);
        setSpaceAdmin(activeSpaces);
        setLoading(false);
      })
      .catch((error) => {
        console.log(error);
        setLoading(false);
        Swal.fire({
          title: "Error",
          text: "Failed to fetch spaces. Please try again.",
          icon: "error",
          timer: 1500,
        });
      });
  }, [authToken]);

  useEffect(() => {
    if (!authToken) {
      Swal.fire({
        title: "Error",
        text: "You need to login first",
        icon: "error",
        timer: 1500,
      });
      return;
    }
    
    // Kullanıcı durumunu kontrol et
    const api = axiosWithAuth();
    api.get("/users/profile")
      .then((response) => {
        if (response.data.status !== 'active') {
          Swal.fire({
            title: "Error",
            text: "Your account is inactive. Please contact support.",
            icon: "error",
            timer: 3000,
          });
          return;
        }
        fetchSpace();
      })
      .catch((error) => {
        console.log(error);
        Swal.fire({
          title: "Error",
          text: "Failed to verify user status. Please try again.",
          icon: "error",
          timer: 1500,
        });
      });
  }, [authToken, fetchSpace]);

  const handleInput = (event) => {
    const { name, value, dataset } = event.target;
    const field = dataset.field;

    if (["az", "en", "ru"].includes(name)) {
      setSpace((prevType) => ({
        ...prevType,
        [field]: { ...prevType[field], [name]: value },
      }));
    } else {
      setSpace((prevType) => ({ ...prevType, [name]: value }));
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!authToken) return;

    setLoading(true);
    const api = axiosWithAuth();
    const method = isEditing ? 'put' : 'post';
    const url = isEditing
      ? `/spaces/${editId}`
      : "/spaces";

    api[method](url, space)
      .then(() => {
        setIsEditing(false);
        setEditId(null);
        setSpace({
          name: { az: "", en: "", ru: "" },
          status: true,
        });
        fetchSpace();
        setLoading(false);
        Swal.fire({
          title: "Success",
          text: isEditing ? "Space has been updated" : "Space has been added",
          icon: "success",
          timer: 1500,
        });
      })
      .catch((error) => {
        console.log(error);
        setLoading(false);
        Swal.fire({
          title: "Error",
          text: isEditing ? "Failed to update space" : "Failed to add space",
          icon: "error",
          timer: 1500,
        });
      });
  };

  const handleEdit = (type) => {
    setSpace(type);
    setEditId(type.id);
    setIsEditing(true);
  };

  const handleDelete = (id) => {
    if (!authToken) return;

    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#cda742",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel"
    }).then((result) => {
      if (result.isConfirmed) {
        setLoading(true);
        const api = axiosWithAuth();
        api.delete(`/spaces/${id}`)
          .then(() => {
            // Silinen kaydı listeden kaldır
            setSpaceAdmin(prevSpaces => prevSpaces.filter(space => space.id !== id));
            setLoading(false);
            Swal.fire({
              title: "Deleted!",
              text: "Space has been deleted.",
              icon: "success",
              timer: 1500,
            });
          })
          .catch((error) => {
            console.log(error);
            setLoading(false);
            Swal.fire({
              title: "Error",
              text: "Failed to delete space",
              icon: "error",
              timer: 1500,
            });
          });
      }
    });
  };

  return (
    <div className="space-manager">
      <div className="container">
        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">Space Manager</h3>
              </div>
              <div className="card-body">
                <form onSubmit={handleSubmit} className="d-flex flex-column gap-3">
                  <div className="space-name">
                    <h5 className="section-title">Space Name</h5>
                    <div className="row g-3">
                      <div className="col-md-4">
                        <div className="form-group">
                          <label className="form-label" htmlFor="az">
                            Azerbaijani
                          </label>
                          <input
                            className="form-control"
                            type="text"
                            value={space.name.az}
                            onChange={handleInput}
                            name="az"
                            data-field="name"
                            required
                            placeholder="Enter Azerbaijani name"
                          />
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="form-group">
                          <label className="form-label" htmlFor="en">
                            English
                          </label>
                          <input
                            className="form-control"
                            type="text"
                            value={space.name.en}
                            onChange={handleInput}
                            name="en"
                            data-field="name"
                            required
                            placeholder="Enter English name"
                          />
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="form-group">
                          <label className="form-label" htmlFor="ru">
                            Russian
                          </label>
                          <input
                            className="form-control"
                            type="text"
                            value={space.name.ru}
                            onChange={handleInput}
                            name="ru"
                            data-field="name"
                            required
                            placeholder="Enter Russian name"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="form-actions">
                    <button type="submit" className="btn btn-primary submit-btn">
                      {isEditing ? "Update Space" : "Add Space"}
                    </button>
                    {isEditing && (
                      <button
                        type="button"
                        className="btn btn-secondary cancel-btn"
                        onClick={() => {
                          setIsEditing(false);
                          setEditId(null);
                          setSpace({
                            name: { az: "", en: "", ru: "" },
                            status: true,
                          });
                        }}
                      >
                        Cancel
                      </button>
                    )}
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>

        <div className="row mt-4">
          <div className="col-12">
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">Space List</h3>
              </div>
              <div className="card-body">
                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead>
                      <tr>
                        <th>Name (AZ)</th>
                        <th>Name (EN)</th>
                        <th>Name (RU)</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {spaceAdmin.map((type) => (
                        <tr key={type.id}>
                          <td>{type.name.az}</td>
                          <td>{type.name.en}</td>
                          <td>{type.name.ru}</td>
                          <td>
                            <span className={`status-badge ${type.status ? "active" : "inactive"}`}>
                              {type.status ? "Active" : "Inactive"}
                            </span>
                          </td>
                          <td>
                            <div className="action-buttons">
                              <button
                                className="btn btn-sm btn-primary edit-btn"
                                onClick={() => handleEdit(type)}
                              >
                                Edit
                              </button>
                              <button
                                className="btn btn-sm btn-danger delete-btn"
                                onClick={() => handleDelete(type.id)}
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SpaceManager;
